import type { BotSkill, FriendView, Identity, RoomSummary } from '@rummy/protocol';
import { useState } from 'react';
import { Portrait } from '../components/Portrait.tsx';
import { Segmented } from '../components/Segmented.tsx';
import { useI18n, type MessageKey } from '../i18n/index.tsx';

export interface LobbyProps {
  identity: Identity;
  friends: FriendView[];
  rooms: RoomSummary[];
  onCreate: () => void;
  onPractice: (seats: 2 | 4 | 6, difficulty: BotSkill) => void;
  onJoin: (roomId: string) => void;
  onWatch: (roomId: string) => void;
  onShowRules: () => void;
}

/**
 * §6.1 screen 2 — the club floor. Not a menu: your face at the door, two big
 * ways to play, the members you know, and the tables still warm.
 */
export function Lobby({
  identity,
  friends,
  rooms,
  onCreate,
  onPractice,
  onJoin,
  onWatch,
  onShowRules,
}: LobbyProps) {
  const { t } = useI18n();
  const [code, setCode] = useState('');
  /**
   * A two-player table is the game two people actually play (engine/config.ts);
   * four fills the board with bots; six is the full table, and used to be the
   * hardest one in the product to assemble — create a room, open the rules
   * drawer, change the seat count, then seat five opponents one at a time.
   * Practice defaults to the smaller table.
   */
  const [practiceSeats, setPracticeSeats] = useState<2 | 4 | 6>(2);
  /** Difficulty is independent of which named characters end up seated. */
  const [practiceDifficulty, setPracticeDifficulty] = useState<BotSkill>('casual');
  const [activityOpen, setActivityOpen] = useState(false);

  const submitCode = () => {
    const trimmed = code.trim().toLowerCase();
    if (trimmed) onJoin(trimmed);
  };

  return (
    <main className="lobby">
      <header className="lobby__hero">
        <Portrait character={identity.avatar} handle={identity.handle} size={56} />
        <div className="lobby__hero-text">
          <h1 className="lobby__greeting">
            {t('lobby.hey')} <em>{identity.handle}</em>
          </h1>
          <p className="lobby__sub">{t('lobby.sub')}</p>
        </div>
        <button type="button" className="btn btn--ghost btn--small lobby__rules-btn" onClick={onShowRules}>
          {t('room.primer.open')}
        </button>
      </header>

      <div className="lobby__actions">
        <button type="button" className="action-card action-card--hot" onClick={onCreate}>
          <span className="action-card__icon" aria-hidden="true">🃏</span>
          <span className="action-card__title">{t('lobby.create')}</span>
          <span className="action-card__note">{t('lobby.create.note')}</span>
        </button>
        <div className="action-card action-card--practice">
          <button
            type="button"
            className="action-card__tap"
            onClick={() => onPractice(practiceSeats, practiceDifficulty)}
          >
            <span className="action-card__icon" aria-hidden="true">🤖</span>
            <span className="action-card__title">{t('lobby.practice')}</span>
            <span className="action-card__note">{t('lobby.practice.note')}</span>
          </button>
          <Segmented
            even
            label={t('room.seats')}
            value={practiceSeats}
            onChange={setPracticeSeats}
            options={[
              { value: 2, label: '2' },
              { value: 4, label: '4' },
              { value: 6, label: '6' },
            ]}
          />
          <Segmented
            even
            label={t('lobby.practice.difficulty')}
            value={practiceDifficulty}
            onChange={setPracticeDifficulty}
            options={[
              { value: 'casual', label: t('lobby.practice.difficulty.casual') },
              { value: 'sharp', label: t('lobby.practice.difficulty.sharp') },
              { value: 'expert', label: t('lobby.practice.difficulty.expert') },
            ]}
          />
        </div>
      </div>

      <section className="panel">
        <h2 className="panel__title">{t('lobby.joinByCode')}</h2>
        <form
          className="lobby__code"
          onSubmit={(e) => {
            e.preventDefault();
            submitCode();
          }}
        >
          <input
            className="field__input"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={t('lobby.codePlaceholder')}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
          />
          <button type="submit" className="btn" disabled={!code.trim()}>
            {t('lobby.join')}
          </button>
        </form>
      </section>

      <button
        type="button"
        className="btn lobby__activity-toggle"
        aria-expanded={activityOpen}
        onClick={() => setActivityOpen((open) => !open)}
      >
        {t('lobby.rooms')}
      </button>

      <div className={`lobby__activity${activityOpen ? ' is-open' : ''}`}>
      <section className="panel">
        <h2 className="panel__title">{t('lobby.friends')}</h2>
        {friends.length === 0 ? (
          <>
            <p className="muted">{t('lobby.friends.empty')}</p>
            {/*
              §4.2 — the graph is closed by construction: friendship is created
              by having played a table together. No search-by-name, no requests
              from strangers.
            */}
            <p className="muted muted--small">{t('lobby.friends.explain')}</p>
          </>
        ) : (
          <ul className="club-grid">
            {friends.map((friend) => (
              <li key={friend.id}>
                <button
                  type="button"
                  className="club-member"
                  onClick={() => friend.roomId && onJoin(friend.roomId)}
                  disabled={!friend.roomId}
                >
                  <span className="club-member__face">
                    <Portrait character={friend.avatar} handle={friend.handle} size={56} />
                    <span
                      className={`club-member__dot club-member__dot--${friend.presence}`}
                      aria-hidden="true"
                    />
                  </span>
                  <span className="club-member__name">{friend.handle}</span>
                  <span className="club-member__state">
                    {t(`presence.${friend.presence}` as MessageKey, {
                      room: friend.roomLabel ?? '',
                    })}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="panel">
        <h2 className="panel__title">{t('lobby.rooms')}</h2>
        {rooms.length === 0 ? (
          <p className="muted">{t('lobby.rooms.empty')}</p>
        ) : (
          <ul className="rooms">
            {rooms.map((room) => (
              <li key={room.id} className="rooms__row">
                <div>
                  <span className="rooms__name">{room.label}</span>
                  <span className="rooms__meta">
                    {t(`format.${room.format}` as MessageKey)} ·{' '}
                    {t('lobby.seatCount', { seated: room.seated, total: room.seats })}
                  </span>
                  {/* What happened last time. The floor used to show only who
                      was online, which is the least interesting thing a club
                      knows about its own tables. */}
                  {room.lastResult && (
                    <span className="rooms__result">
                      {room.lastResult.yourPlace === 1
                        ? t('lobby.result.youWon', { hands: room.lastResult.handsPlayed })
                        : room.lastResult.yourPlace !== null
                          ? t('lobby.result.youPlaced', {
                              winner: room.lastResult.winners[0] ?? '',
                              place: room.lastResult.yourPlace,
                              players: room.lastResult.players,
                            })
                          : t('lobby.result.won', {
                              winner: room.lastResult.winners[0] ?? '',
                            })}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn--small"
                  onClick={() => (room.state === 'in_hand' ? onWatch(room.id) : onJoin(room.id))}
                >
                  {room.state === 'in_hand' ? t('lobby.watch') : t('lobby.join')}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
      </div>
    </main>
  );
}
